// src/pages/ManifestoPage.jsx

import { Link } from "react-router-dom";
import PageTracker from '../../components/tracking/PageTracker';
import ScrollTracker from '../../components/tracking/ScrollTracker';

const ManifestoPage = () => {
  return (
    <main className="manifesto-page">
      <PageTracker pageName="ManifestoPage" />
      
      {/* HERO */}
      <header className="manifesto-hero">
        <div className="manifesto-hero-inner">

          <p className="manifesto-kicker">A Founding Manifesto · Version 2.0</p>
          <h1>The Listening Revolution</h1>
          <p className="manifesto-subtitle">
            Human listening as the foundation for perceptual voice data, training, research, and care.
          </p>

          <div className="manifesto-hero-actions web-cta-row">
            <Link className="web-button web-button--primary" to="/signup">
              Join All-Voiced
            </Link>

            <a className="web-button web-button--secondary" href="#manifesto-text">
              Start reading
            </a>
          </div>

        </div>
      </header>

      {/* CONTENT */}
      <section className="manifesto-section" id="manifesto-text">
        <article className="manifesto-content">
          <div className="manifesto-heading">
            <h2>The Listening Revolution</h2>
            <p className="manifesto-meta">
              <span className="manifesto-tag">Version 2.0</span>
              <span className="manifesto-dot">•</span>
              <span>2026</span>
            </p>
          </div>

          <div className="manifesto-body">
            <p>
              All-Voiced began as a research and educational project — born out of a need
              many of us have felt.
            </p>

            <p>
              A need to take listening in voice seriously, even when it is labeled as
              “subjective,” as if that meant less rigorous or less important. A need for better
              infrastructure to train it, calibrate it, and study it with the structure and depth it deserves.
            </p>

            <p>Over time, something became clear:</p>

            <p className="manifesto-lines">
              <strong>Listening is not passive.</strong>
              <br />
              <strong>Listening is not vague.</strong>
              <br />
              <strong>Listening is not an afterthought.</strong>
            </p>

            <p>
              Listening begins the moment a voice enters the room.
            </p>

            <p className="manifesto-lines manifesto-lines--soft">
              Before the microphone.
              <br />
              Before the measurement.
              <br />
              Before the graph.
            </p>

            <p className="manifesto-strong">
              <strong>Listening is a skill.</strong>
            </p>

            <p>
              It can be trained.
              <br />
              It can be refined.
              <br />
              It can be studied with <strong>care and precision</strong>.
            </p>

            <p>
              And when we treat listening as foundational — not auxiliary — everything changes.
            </p>

            <p className="manifesto-lines manifesto-lines--soft">
              In <strong>research</strong>, listening shapes what we measure and how we interpret data. 
              <br />
              In <strong>education</strong>, listening shapes how future clinicians learn to hear.
              <br />
              In <strong>clinical practice</strong>, listening shapes decisions that affect real people.
            </p>

            <p>
              In a world that moves fast — that favors speed, automation, and surface
              impressions — choosing to listen carefully is almost radical.
            </p>

            <p>
              That is the <strong>Listening Revolution</strong>.
            </p>

            <p>
              It is not about rejecting technology. It is about using technology to
              support deeper listening — not replace it.
            </p>

            <p>
              It is not about eliminating subjectivity. It is about understanding it.
            </p>

            <p>
              Because subjectivity does not mean randomness. It means <strong>perspective</strong>.
            </p>

            <p>
              And when perspectives are compared, documented, and shared, they become knowledge.
            </p>

            <div className="manifesto-callout">
              <p>
                <strong>All-Voiced</strong> carries more than one meaning.
              </p>

              <p>
                In phonetics, “voiced” describes sounds produced with vocal fold vibration.
                The term “all-voiced” is sometimes used to describe speech composed entirely
                of voiced sounds.
              </p>

            <p>
              But All-Voiced is more than a phonetic description.
              It suggests a space where every listener has a voice in how knowledge is built.
            </p>

            <p>
              Not a single authority.
              Not isolated judgments.
              But perception brought together — examined, contrasted, refined.
            </p>

            <p>
              When many perspectives meet, subjectivity does not disappear.
              It becomes something we can build on.
            </p>
            </div>

            <p>
              The Listening Revolution begins in voice. But it speaks to something broader:
            </p>

            <p className="manifesto-lines">
              A commitment to paying attention.
              <br />
              To slowing down.
              <br />
              To treating perception as something worthy of care and scientific respect.
            </p>

            <p>
              All-Voiced is one expression of that commitment. 
              The training tools, evaluator ecosystems, and perceptual data workflows are
              <strong> infrastructure for something deeper</strong>: a way to turn 
              <strong> human listening into structured knowledge</strong>.
            </p>

            <p className="manifesto-strong">
              <strong>As voice technology continues to advance, this matters more than ever.</strong>
            </p>

            <p>
              Voice technologies can process signals at scale, but they still need 
              <strong> meaningful human reference points</strong>. In voice, those reference points
              are not only acoustic. They are <strong>perceptual, clinical, cultural, and human</strong>.
            </p>

            <p>
              All-Voiced exists to build that bridge: between <strong>listening and data</strong>,
              between <strong>training and research</strong>, between <strong>human perception and voice technology</strong>.
            </p>

            <p className="manifesto-lines">
              <strong>Listening transforms science.</strong>
              <br />
              <strong>Listening transforms learning.</strong>
              <br />
              <strong>Listening transforms care.</strong>
            </p>

            <p className="manifesto-closing">
              If you are here, you are already part of it.
            </p>
          </div>

          <div className="manifesto-signature">
            <p className="manifesto-quote">
              In a world that moves fast, we choose to listen carefully.
            </p>
            <p className="manifesto-sig">
              — Neus Calaf, PhD, SLP
            </p>
            <p className="manifesto-sig-role">
              Founder & Project Lead, All-Voiced
            </p>
          </div>

          <div className="manifesto-footer">
            <p className="manifesto-note">
              This is a living document. It will evolve as the All-Voiced ecosystem grows.
            </p>
            <div className="manifesto-footer-actions web-cta-row">
              <Link className="web-button web-button--primary" to="/signup">
                Join All-Voiced
              </Link>

              <Link className="web-button web-button--secondary" to="/services">
                Explore applications
              </Link>
            </div>
          </div>
        </article>
      </section>
      <ScrollTracker pageName="ManifestoPage" />
    </main>
  );
};

export default ManifestoPage;
